import React, { useState, useMemo } from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import Icon from "components/AppIcon";

const TIMEFRAMES = ["1H", "4H", "1D", "1W"];
const INDICATORS = ["SMA", "EMA", "RSI", "MACD"];

const generateCandles = (tf) => {
  const count = tf === "1H" ? 48 : tf === "4H" ? 42 : tf === "1D" ? 60 : 52;
  const base = 142.5;
  let price = base;
  const now = Date.now();
  const intervalMs = tf === "1H" ? 3600000 : tf === "4H" ? 14400000 : tf === "1D" ? 86400000 : 604800000;
  return Array.from({ length: count }, (_, i) => {
    const change = (Math.random() - 0.48) * 4;
    const open = price;
    price = Math.max(80, price + change);
    const close = price;
    const high = Math.max(open, close) + Math.random() * 2;
    const low = Math.min(open, close) - Math.random() * 2;
    const volume = Math.floor(Math.random() * 80000 + 20000);
    const ts = new Date(now - (count - i) * intervalMs);
    const label = tf === "1D" ? ts?.toLocaleDateString("en-US", { month: "short", day: "numeric" })
      : tf === "1W" ? ts?.toLocaleDateString("en-US", { month: "short", day: "numeric" })
      : ts?.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    return { time: label, open: +open?.toFixed(2), close: +close?.toFixed(2), high: +high?.toFixed(2), low: +low?.toFixed(2), volume, bullish: close >= open };
  });
};

const calcSMA = (data, period = 14) =>
  data?.map((d, i) => {
    if (i < period - 1) return { ...d, sma: null };
    const avg = data?.slice(i - period + 1, i + 1)?.reduce((s, x) => s + x?.close, 0) / period;
    return { ...d, sma: +avg?.toFixed(2) };
  });

const calcEMA = (data, period = 14) => {
  const k = 2 / (period + 1);
  let ema = data?.[0]?.close || 0;
  return data?.map((d, i) => {
    if (i === 0) { ema = d?.close; return { ...d, ema: +ema?.toFixed(2) }; }
    ema = d?.close * k + ema * (1 - k);
    return { ...d, ema: +ema?.toFixed(2) };
  });
};

const CustomCandlestick = (props) => {
  const { x, y, width, height, open, close, high, low, bullish } = props;
  if (!width || !height) return null;
  const color = bullish ? "var(--color-success)" : "var(--color-error)";
  const bodyTop = Math.min(y, y + height);
  const bodyH = Math.abs(height) || 1;
  const midX = x + width / 2;
  return (
    <g>
      <line x1={midX} y1={y - (high - Math.max(open, close)) * 10} x2={midX} y2={bodyTop} stroke={color} strokeWidth={1} />
      <rect x={x} y={bodyTop} width={width} height={bodyH} fill={color} opacity={0.85} rx={1} />
      <line x1={midX} y1={bodyTop + bodyH} x2={midX} y2={bodyTop + bodyH + (Math.min(open, close) - low) * 10} stroke={color} strokeWidth={1} />
    </g>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  let d = payload?.[0]?.payload;
  if (!d) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-3 text-xs font-mono shadow-lg" style={{ minWidth: 160 }}>
      <div className="text-muted-foreground mb-1">{d?.time}</div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
        <span className="text-muted-foreground">O</span><span className="text-foreground">${d?.open}</span>
        <span className="text-muted-foreground">H</span><span className="text-success" style={{ color: "var(--color-success)" }}>${d?.high}</span>
        <span className="text-muted-foreground">L</span><span style={{ color: "var(--color-error)" }}>${d?.low}</span>
        <span className="text-muted-foreground">C</span><span className="text-foreground">${d?.close}</span>
        <span className="text-muted-foreground">Vol</span><span className="text-primary" style={{ color: "var(--color-primary)" }}>{d?.volume?.toLocaleString()}</span>
      </div>
      {d?.sma && <div className="mt-1 text-amber-400">SMA: ${d?.sma}</div>}
      {d?.ema && <div className="text-cyan-400" style={{ color: "var(--color-primary)" }}>EMA: ${d?.ema}</div>}
    </div>
  );
};

const CandlestickChart = ({ symbol }) => {
  const [timeframe, setTimeframe] = useState("1D");
  const [activeIndicators, setActiveIndicators] = useState(["SMA"]);

  const rawData = useMemo(() => generateCandles(timeframe), [timeframe]);
  const data = useMemo(() => {
    let d = rawData;
    if (activeIndicators?.includes("SMA")) d = calcSMA(d);
    if (activeIndicators?.includes("EMA")) d = calcEMA(d);
    return d;
  }, [rawData, activeIndicators]);

  const toggleIndicator = (ind) =>
    setActiveIndicators(prev => prev?.includes(ind) ? prev?.filter(i => i !== ind) : [...prev, ind]);

  const tickInterval = data?.length > 40 ? Math.floor(data?.length / 8) : Math.floor(data?.length / 6);

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-5">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Icon name="BarChart2" size={16} color="var(--color-primary)" />
          <span className="font-heading text-sm font-semibold text-foreground">{symbol} Chart</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Timeframes */}
          <div className="flex bg-secondary rounded-lg p-0.5 gap-0.5">
            {TIMEFRAMES?.map(tf => (
              <button key={tf} onClick={() => setTimeframe(tf)}
                className={`px-2.5 py-1 rounded-md text-xs font-mono font-medium transition-all duration-150 ${timeframe === tf ? "bg-primary text-background" : "text-muted-foreground hover:text-foreground"}`}
                style={timeframe === tf ? { background: "var(--color-primary)", color: "var(--color-primary-foreground)" } : {}}>
                {tf}
              </button>
            ))}
          </div>
          {/* Indicators */}
          <div className="flex gap-1">
            {INDICATORS?.map(ind => (
              <button key={ind} onClick={() => toggleIndicator(ind)}
                className={`px-2 py-1 rounded-md text-xs font-mono transition-all duration-150 border ${activeIndicators?.includes(ind) ? "border-primary/50 text-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary/30"}`}
                style={activeIndicators?.includes(ind) ? { color: "var(--color-primary)", borderColor: "rgba(0,217,255,0.4)", background: "rgba(0,217,255,0.08)" } : {}}>
                {ind}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Main Chart */}
      <div className="w-full h-56 md:h-72 lg:h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,217,255,0.06)" />
            <XAxis dataKey="time" tick={{ fill: "var(--color-muted-foreground)", fontSize: 10, fontFamily: "var(--font-caption)" }}
              interval={tickInterval} axisLine={false} tickLine={false} />
            <YAxis domain={["auto", "auto"]} tick={{ fill: "var(--color-muted-foreground)", fontSize: 10, fontFamily: "var(--font-caption)" }}
              axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} width={52} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="close" shape={<CustomCandlestick />} isAnimationActive={false} />
            {activeIndicators?.includes("SMA") && <Line type="monotone" dataKey="sma" stroke="#F59E0B" strokeWidth={1.5} dot={false} connectNulls />}
            {activeIndicators?.includes("EMA") && <Line type="monotone" dataKey="ema" stroke="var(--color-primary)" strokeWidth={1.5} dot={false} connectNulls />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {/* Volume Chart */}
      <div className="w-full h-16 mt-2">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
            <XAxis dataKey="time" hide />
            <YAxis hide />
            <Bar dataKey="volume" fill="rgba(0,217,255,0.2)" radius={[1, 1, 0, 0]} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="text-xs text-muted-foreground font-caption mt-1 text-center">Volume</div>
    </div>
  );
};

export default CandlestickChart;